
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import AdminLayout from "@/layouts/AdminLayout"
import { NewCategoryCard } from "@/components/categories/NewCategoryCard"
import { ExistingCategoryCard } from "@/components/categories/ExistingCategoryCard"
import type { Database } from "@/integrations/supabase/types"

type ClientCategory = Database["public"]["Tables"]["client_categories"]["Row"]
type SubscriptionTier = Database["public"]["Enums"]["subscription_tier"]

export default function ManageClientCategories() {
  const { toast } = useToast()

  const { data: categories, isLoading } = useQuery({
    queryKey: ["client_categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_categories")
        .select("*")
        .order("name")

      if (error) {
        toast({
          variant: "destructive",
          title: "Error loading categories",
          description: error.message,
        })
        return []
      }

      return data as ClientCategory[]
    },
  })

  const handleSaveCategory = async (categoryId?: string) => {
    const formData = new FormData(document.getElementById(`category-form-${categoryId || 'new'}`) as HTMLFormElement)
    const name = formData.get("name") as string
    const description = formData.get("description") as string
    const tier = formData.get("tier") as SubscriptionTier
    const active = formData.get("active") === "true"

    const { error } = await supabase.from("client_categories").upsert({
      id: categoryId,
      name,
      description,
      subscription_tier: tier,
      active,
    })

    if (error) {
      toast({
        variant: "destructive",
        title: "Error saving category",
        description: error.message,
      })
    } else {
      toast({
        title: "Category saved successfully",
      })
    }
  }

  return (
    <AdminLayout title="Manage Client Categories">
      <NewCategoryCard onSave={() => handleSaveCategory()} />
      
      <div className="grid gap-6">
        {categories?.map((category) => (
          <ExistingCategoryCard
            key={category.id}
            category={category}
            onSave={() => handleSaveCategory(category.id)}
          />
        ))}
      </div>
    </AdminLayout>
  )
}
